import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DataProvider } from "@/contexts/DataProvider";
import { NotificationProvider } from "@/contexts/NotificationContext";

// Pages
import Index from "./pages/Index";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Screens from "./pages/Screens";
import Ads from "./pages/Ads";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import FavoriteScreens from "./pages/FavoriteScreens";
import MySales from "./pages/MySales";
import ExpiredAds from "./pages/ExpiredAds";
import Users from "./pages/Users";
import FacturacionPage from "./pages/facturacion/page";
import MyClients from "./pages/MyClients";
import OrdenCompra from "./pages/OrdenCompra";
import NotaCredito from "./pages/NotaCredito";
import Cotizacion from "./pages/Cotizacion";
import CompanyConfig from "./pages/CompanyConfig";

import VNNOXPlayers from "./pages/VNNOXPlayers";
import ScreenMaintenance from "./pages/ScreenMaintenance";


const queryClient = new QueryClient();

// Protected route component
const ProtectedRoute = ({ children, adminOnly = false, adminAndSellerOnly = false, mantenimientoOnly = false }: { 
  children: JSX.Element, 
  adminOnly?: boolean, 
  adminAndSellerOnly?: boolean,
  mantenimientoOnly?: boolean 
}) => {
  const { isAuthenticated, isAdmin, isSeller, isMantenimiento, user } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  if (adminAndSellerOnly && user?.profile?.role !== 'admin' && user?.profile?.role !== 'seller') {
    return <Navigate to="/dashboard" replace />;
  }

  if (mantenimientoOnly && !isMantenimiento && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/" element={<Index />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/screens"
        element={
          <ProtectedRoute adminOnly={true}>
            <Screens />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ads"
        element={
          <ProtectedRoute>
            <Ads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expired-ads"
        element={
          <ProtectedRoute>
            <ExpiredAds />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute adminOnly={true}>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorite-screens"
        element={
          <ProtectedRoute>
            <FavoriteScreens />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-sales"
        element={
          <ProtectedRoute>
            <MySales />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute adminOnly={true}>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/facturacion"
        element={
          <ProtectedRoute>
            <FacturacionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-clients"
        element={
          <ProtectedRoute adminAndSellerOnly={true}>
            <MyClients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orden-compra"
        element={
          <ProtectedRoute>
            <OrdenCompra />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nota-credito"
        element={
          <ProtectedRoute>
            <NotaCredito />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cotizacion"
        element={
          <ProtectedRoute>
            <Cotizacion />
          </ProtectedRoute>
        }
      />
      <Route
        path="/company-config"
        element={
          <ProtectedRoute adminOnly={true}>
            <CompanyConfig />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/vnnox-players"
        element={
          <ProtectedRoute adminOnly={true}>
            <VNNOXPlayers />
          </ProtectedRoute>
        }
      />

      <Route
        path="/screen-maintenance"
        element={
          <ProtectedRoute mantenimientoOnly={true}>
            <ScreenMaintenance />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <DataProvider>
            <NotificationProvider>
              <AppRoutes />
            </NotificationProvider>
          </DataProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
